import React, { useState } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  useMediaQuery,
  FormHelperText,
  Link,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SuccessAnimation from './SuccessAnimation';

function validateEmail(email) {
  // Simple email regex
  return /^\S+@\S+\.\S+$/.test(email);
}

function validatePhone(phone) {
  // Accepts numbers, spaces, dashes, parentheses, and plus
  return /^[0-9\s\-()+]{8,}$/.test(phone);
}

function LandingPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const validate = () => {
    let temp = {};
    temp.firstName = formData.firstName ? '' : 'First name is required';
    temp.lastName = formData.lastName ? '' : 'Last name is required';
    temp.email = formData.email ? (validateEmail(formData.email) ? '' : 'Invalid email address') : 'Email is required';
    temp.phone = formData.phone ? (validatePhone(formData.phone) ? '' : 'Invalid phone number') : 'Phone is required';
    setErrors(temp);
    return Object.values(temp).every(x => x === '');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    const submissions = JSON.parse(localStorage.getItem('submissions') || '[]');
    submissions.push({ ...formData, timestamp: new Date().toISOString() });
    localStorage.setItem('submissions', JSON.stringify(submissions));
    setFormData({ firstName: '', lastName: '', email: '', phone: '' });
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 1800);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  return (
    <Container maxWidth="md" sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ mt: isMobile ? 2 : 4, mb: isMobile ? 1 : 2 }}>
          <img
            src="/logo.png"
            alt="Love Clinic & Med Spa"
            style={{ width: '100%', maxWidth: isMobile ? '220px' : '340px', display: 'block', margin: '0 auto' }}
          />
        </Box>
        <Paper elevation={3} sx={{
          p: isMobile ? 2 : 6,
          borderRadius: 3,
          maxWidth: isMobile ? '100%' : 500,
          margin: '0 auto',
          minHeight: isMobile ? 350 : 480,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}>
          {submitted ? (
            <>
              <SuccessAnimation />
              <Typography align="center" color="#27ae60" sx={{ mt: 2, fontWeight: 600 }}>
                Submission successful!
              </Typography>
            </>
          ) : (
            <>
              <Typography variant={isMobile ? "h5" : "h4"} align="center" gutterBottom>
                Giveaway!
              </Typography>
              <Typography variant="subtitle1" align="center" sx={{ color: 'black', fontWeight: 'bold', mb: isMobile ? 2 : 4 }}>
                Enter for a chance to win a free IV therapy
              </Typography>
              <form onSubmit={handleSubmit} noValidate>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  error={!!errors.firstName}
                  sx={{ mb: 1 }}
                />
                {errors.firstName && <FormHelperText error sx={{ mb: 1 }}>{errors.firstName}</FormHelperText>}
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  error={!!errors.lastName}
                  sx={{ mb: 1 }}
                />
                {errors.lastName && <FormHelperText error sx={{ mb: 1 }}>{errors.lastName}</FormHelperText>}
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  error={!!errors.email}
                  sx={{ mb: 1 }}
                />
                {errors.email && <FormHelperText error sx={{ mb: 1 }}>{errors.email}</FormHelperText>}
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  error={!!errors.phone}
                  sx={{ mb: 2 }}
                />
                {errors.phone && <FormHelperText error sx={{ mb: 1 }}>{errors.phone}</FormHelperText>}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  sx={{
                    backgroundColor: '#D4AF37',
                    '&:hover': {
                      backgroundColor: '#B4941F',
                    },
                    fontSize: isMobile ? '1rem' : '1.15rem',
                    py: isMobile ? 1.2 : 1.6,
                  }}
                >
                  Submit
                </Button>
              </form>
            </>
          )}
          <Typography variant="caption" sx={{ mt: 4, color: '#888', textAlign: 'center', display: 'block' }}>
            By submitting, you agree to our privacy policy. Your information is kept confidential and will not be shared.
          </Typography>
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Link
              href="https://www.loveclinicmedspa.com/"
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              sx={{
                display: 'inline-block',
                backgroundColor: '#D4AF37',
                color: '#fff',
                px: 3,
                py: 1.2,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: isMobile ? '1rem' : '1.1rem',
                letterSpacing: 1,
                boxShadow: 1,
                transition: 'background 0.2s',
                '&:hover': {
                  backgroundColor: '#B4941F',
                  color: '#fff',
                },
              }}
            >
              Visit Our Main Website
            </Link>
          </Box>
        </Paper>
        <Typography variant="caption" sx={{ mt: 2, color: '#888', textAlign: 'center', display: 'block' }}>
          designed by DynamicEndpoints
        </Typography>
      </Box>
    </Container>
  );
}

export default LandingPage;
